import React from "react";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { StyledGrid } from "components/simple";
import { color, spacingDefaults } from "style/constants";
import { ScoreRangeType } from "types";

interface LineProps {
  label: string;
  isActive: boolean;
  activeColor: color;
  score: number;
  min: number;
  max: number;
}

const Line: React.FC<LineProps> = ({
  label,
  isActive,
  activeColor,
  min,
  max,
  score
}: LineProps) => {
  if (isActive) {
    const percentage = ((score - min) * 100) / (max - min);

    const minPercentage = 0;
    const maxPercentage = 100;
    const value = Math.min(Math.max(percentage, minPercentage), maxPercentage);
    return (
      <StyledGrid position="relative">
        <ArrowDropDownIcon
          style={{ position: "absolute", top: "-20px", left: `calc(${value}% - 12px)` }}
        />
        <StyledGrid
          aria-label={label}
          item
          height="5px"
          backgroundColor={activeColor}
          width="100px"
          borderRadius="60px"
        ></StyledGrid>
      </StyledGrid>
    );
  } else {
    return (
      <StyledGrid
        aria-label={label}
        item
        height="5px"
        backgroundColor={color.GRAY}
        width="100px"
        borderRadius="60px"
      ></StyledGrid>
    );
  }
};

interface ScoreRangeProps {
  ranges: ScoreRangeType;
  score: number;
}

function isActive(score: number, isReverse: boolean, min: number, max: number): boolean {
  const minValue = isReverse ? max : min;
  const maxValue = isReverse ? min : max;

  return score >= minValue && score <= maxValue;
}

const ScoreRange: React.FC<ScoreRangeProps> = ({ ranges, score }: ScoreRangeProps) => {
  if (!ranges) return null;

  const { underperforming: under, competent, accelerated } = ranges;

  const isUnder = isActive(score, ranges.reverse, under[0], under[1]);
  const isCompetent = isActive(score, ranges.reverse, competent[0], competent[1]);
  const isAccelerated = isActive(score, ranges.reverse, accelerated[0], accelerated[1]);

  if (!isUnder && !isCompetent && !isAccelerated) return null;

  const missingAccelerated = accelerated.length === 0;

  return (
    <>
      <StyledGrid container width={missingAccelerated ? "200px" : "300px"} direction="row">
        <Line
          label={"Underperforming"}
          isActive={isUnder}
          activeColor={color.RED}
          min={under[0]}
          max={competent[0]}
          score={score}
        />
        <Line
          label={"Competent"}
          isActive={isCompetent}
          activeColor={color.YELLOW}
          min={competent[0]}
          max={missingAccelerated ? competent[1] : accelerated[0]}
          score={score}
        />

        {!missingAccelerated && (
          <Line
            label={"Accelerated"}
            isActive={isAccelerated}
            activeColor={color.GREEN}
            min={accelerated[0]}
            max={accelerated[1]}
            score={score}
          />
        )}
      </StyledGrid>
      <StyledGrid container justifyContent="space-between" paddingTop={spacingDefaults.small}>
        <span>{under[0]}</span>
        <span>{competent[0]}</span>
        <span>{competent[1]}</span>
        {!missingAccelerated && (
          <span>{accelerated[1] > 500 ? <span>&#8734;</span> : accelerated[1]}</span>
        )}
      </StyledGrid>
    </>
  );
};

export default ScoreRange;
